import { IdentityApi, DiscoveryApi } from '@backstage/core-plugin-api';
import {
  UserProfile,
  UserSkill,
} from '@dewise-devex/backstage-plugin-user-profile-common';
import {
  ApiUtil,
  DateUtil,
  StringUtil,
} from '@dewise-devex/backstage-plugin-utilities-common-client';
import fetch from 'node-fetch';
import { ExtendedUserEntity, UserEntity } from '@backstage/catalog-model';

export type ExtendedProfile = UserProfile & {
  picture: string;
};

export interface IUserProfileService {
  getProfile(entity: ExtendedUserEntity): Promise<ExtendedProfile | undefined>;
  getProfilePicture(email: string): Promise<{ data: string }>;
  updateProfile(
    entity: ExtendedUserEntity,
    params: UpdateProfileReqParams,
  ): Promise<{ message: string; data: UserProfile }>;
  getCredlyCertInfo(certUrl: string): Promise<UserCertification>;
  addCert(params: AddCertReqParams): Promise<any>;
  getSkillList(): Promise<UserSkill[]>;
  updateSkills(
    params: UpdateSkillReqParams,
  ): Promise<{ message: string; data: UpdateSkillsReturnType }>;
}

export class UserProfileService implements IUserProfileService {
  private readonly identityApi: IdentityApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly userPluginId = 'profile';

  constructor(options: {
    identityApi: IdentityApi;
    discoveryApi: DiscoveryApi;
  }) {
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async authenticate(
    url: fetch.RequestInfo,
    options?: fetch.RequestInit,
  ) {
    const authorizedRequest = await ApiUtil.authorizeRequest(this.identityApi);
    return await authorizedRequest.execute(url, options);
  }

  async getProfile(
    entity: ExtendedUserEntity,
  ): ReturnType<IUserProfileService['getProfile']> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    return await this.authenticate(
      `${profileUrl}/users/${StringUtil.emailToName(entity.spec.profile?.email ?? '')}`,
    )
      .then(async response => {
        if (response.status === 404) {
          return createDefaultProfile(entity);
        }

        const profilePicture = await this.getProfilePicture(
          entity.spec.profile?.upn ?? '',
        );

        return {
          ...response,
          picture: profilePicture ?? entity.spec.profile?.picture ?? '',
        };
      })
      .catch(error => {
        console.error('Error fetching user profile:', error);
        return createDefaultProfile(entity);
      });
  }

  async updateProfile(
    entity: ExtendedUserEntity,
    params: UpdateProfileReqParams,
  ): Promise<{ message: string; data: UserProfile }> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    const response = await this.authenticate(
      `${profileUrl}/users/${StringUtil.emailToName(entity.spec.profile?.email ?? '')}`,
      {
        method: 'PUT',
        body: JSON.stringify(params),
      },
    );

    return {
      message: 'Successfully saved!',
      data: response as UserProfile,
    };
  }

  async getCredlyCertInfo(certUrl: string): Promise<UserCertification> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    return await this.authenticate(
      `${profileUrl}/credly-certification/?url=${certUrl}`,
    );
  }

  async addCert(
    params: AddCertReqParams,
  ): Promise<{ message: string; data: AddCertReturnType }> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    const response = await this.authenticate(
      `${profileUrl}/credly-certification/${StringUtil.emailToName(params.email ?? '')}`,
      {
        method: 'POST',
        body: JSON.stringify({ certificationUrl: params.certUrl }),
      },
    );

    return {
      message: 'Successfully saved!',
      data: response as AddCertReturnType,
    };
  }

  async getSkillList(): ReturnType<IUserProfileService['getSkillList']> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    return await this.authenticate(`${profileUrl}/skills`);
  }

  async updateSkills(
    params: UpdateSkillReqParams,
  ): Promise<{ message: string; data: UpdateSkillsReturnType }> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);

    const response = await this.authenticate(
      `${profileUrl}/skills/${StringUtil.emailToName(params.email ?? '')}`,
      {
        method: 'PUT',
        body: JSON.stringify(params.skills),
      },
    );

    return {
      message: 'Successfully saved!',
      data: response as UpdateSkillsReturnType,
    };
  }

  async getProfilePicture(
    email: string,
  ): ReturnType<IUserProfileService['getProfilePicture']> {
    const profileUrl = await this.discoveryApi.getBaseUrl(this.userPluginId);
    return await this.authenticate(
      `${profileUrl}/services/msgraph/photo/${email}`,
    )
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching user profile picture:', error);
        return { data: '' };
      });
  }
}

function createDefaultProfile(entity: UserEntity): ExtendedProfile {
  return {
    id: '0',
    email: entity.spec.profile?.email ?? '',
    displayName: entity.spec.profile?.displayName ?? '',
    proficiencies: [],
    bio: '',
    role: '',
    timezone: DateUtil.toCurrentTime(),
    location: '',
    username: '',
    skills: [],
    certifications: [],
    website: '',
    socialMedia: [],
    picture: '',
  };
}
